import axios from "axios";
import moment from "moment";
import store from "../../store";
import { logDetailed } from "../../helpers/utils";
import { formatDateDisplay, formatDateISO } from "../../helpers/dateUtils";
import { iPadBrowser } from "../../helpers/browserDetect";

export const submitQuickSearch = ({ commit, dispatch }, text) => {
  logDetailed("actions", "submitQuickSearch", text);
  commit("quickSearch", text);
  commit("clearSelectedPatient");
  return dispatch("getPatients");
};

export const submitAdvancedSearch = ({ commit, dispatch }, search) => {
  logDetailed("actions", "submitAdvancedSearch", search);
  commit("advancedSearch", search);
  commit("clearSelectedPatient");
  return dispatch("getPatients");
};

export const submitTimespanSearch = ({ commit, dispatch }, timespan) => {
  logDetailed("actions", "submitTimespanSearch", timespan);
  commit("setSearchTimespan", timespan);
  commit("clearSelectedPatient");
  return dispatch("getPatients");
}

export const getPatients = ({ getters, commit, dispatch }, autoRefresh = false) => {
  if (getters.ssoEmrPro)
    commit("ssoSearch", getters.emrProSearchParams);
  let search = { ...getters.patientSearch, ...getters.patientSearchOptions };
  logDetailed("actions", "getPatients", "autoRefresh", autoRefresh, "getters.ssoEmrPro", getters.ssoEmrPro, "getters.emrProSearchParams", getters.emrProSearchParams, "getters.ssoSearch", getters.ssoSearch, "search", search);
  let selectedExams = getters.selectedExams;
  let oldPatient = getters.patient;
  let ssoExSrNo = getters.ssoExSrNo;
  commit("setPatients", []);
  commit("startLoadingPatients");
  let promise = axios
    .post("/api/patient/list", search)
    .then(response => {
      commit("setPatients", response.data.patients.map(value => {
        return {
          ptId: value.ptID,
          ptSrNo: value.ptSrNo,
          exDate: value.exDate,
          exMoment: moment(value.exDate),
          exDateDisplay: formatDateDisplay(value.exDate),
          exDateIso: formatDateISO(value.exDate),
          lastName: decodeURIComponent(value.nameLast),
          firstName: decodeURIComponent(value.nameFirst),
          gender: value.gender,
          dob: formatDateDisplay(value.dob),
          isoDOB: formatDateISO(value.dob),
          status: value.ptValidityStatus + "",
          tableKey: value.ptSrNo + "_" + formatDateISO(value.exDate)
        };
      }));
      commit("setTotalPatients", response.data.totalPatients);
      if (oldPatient) {
        if (getters.autoRefreshPatientList == 1 && !!autoRefresh)
          commit("selectPatient", oldPatient);
        else
          dispatch("selectPatient", oldPatient).then(() => commit("selectExams", selectedExams));
      } else if (allSamePatient(getters.patients)) {
        dispatch("selectPatient", getters.patients[0]).then(() =>{
          if(ssoExSrNo){
            let selectedSSOExam = getters.examBySrNo(parseInt(ssoExSrNo));
            commit("selectExams", [selectedSSOExam]);   
            if(getters.exams.length > 0)         
              dispatch("openProofsheetFromExamList");
            else
              commit("displayStatus", { message: "Exam not found", level: "error" });
          }
        });
      } else {
        commit("clearSelectedPatient");
      }
      if (getters.ssoSearch && getters.patients.length == 0)
        commit("displayStatus", { message: "Patient not found.", level: "error" });
      if (getters.ssoSearch)
        commit("clearSSOSearch");
    })
    .catch(error =>{      
      commit("displayStatus", { message: "The patient exam data is not found.", level: "error" });
      Promise.reject(error.response);
    });
  promise.finally(() => commit("stopLoadingPatients"));
  return promise;
};

function allSamePatient(patients) {
  if (!patients || !patients.length) return false;
  if (patients.length === 1) return true;
  let ptSrNo = patients[0].ptSrNo;
  return patients.every(p => p.ptSrNo === ptSrNo);
}

export const selectPatient = ({ commit, dispatch, getters }, patient) => {
  
  commit("selectExams", []);
  commit("selectPatient", patient);
  if (getters.emrProExSrNo){
    return dispatch("getPatientExamSSO");
  }
  return dispatch("getPatientExams");
};

export const getPatientExams = ({ dispatch, getters }) => {
  let url = "/api/exam/list/" + getters.ptSrNo;
  return dispatch("loadExams", url);
};

export const getPatientExamSSO = ({ dispatch, getters }) => {
  let url = "/api/exam/" + getters.emrProExSrNo;
  return dispatch("loadExams", url);
};

export const loadExams = ({ commit, dispatch }, url) => {
  logDetailed("getPatientExamActions", "loadExams", "execution-time", url, "beforeLoad");
  let beforeLoad = Date.now();
  commit("setExams", []);
  commit("startLoadingExams");

  let apiCall = axios.post(url);
  apiCall.finally(() => {
    let elapsed = Date.now() - beforeLoad;
    logDetailed("getPatientExamActions", "loadExams", "execution-time", url, "api call", elapsed);
  });

  let apiThenProcessExams = apiCall.then(response => dispatch("processExams", response.data))
  apiThenProcessExams.finally(() => {
    let elapsed = Date.now() - beforeLoad;
    logDetailed("getPatientExamActions", "loadExams", "execution-time", url, "api call + process exams", elapsed);
  });

  let promise = apiThenProcessExams
    .then(() => dispatch("getOCTExamInfo"))
    .catch(error => Promise.reject(error.response));
  promise.finally(() => {
    let elapsed = Date.now() - beforeLoad;
    logDetailed("getPatientExamActions", "loadExams", "execution-time", url, "whole procedure", elapsed);
    commit("stopLoadingExams")
  });
  return promise;
};

function getPhysicianName(phySrNo) {
  if (store.getters.physicians.length > 0) 
  {
    let physician = store.getters.physicians.find(
      obj => obj.phySrNo == phySrNo
    );
    return physician.firstName + " " + physician.lastName;
  }
  return ""
}

function generatePhysicianNameArray(array) {
  var container = [];
  array.forEach(value => {
    container.push(getPhysicianName(value));
  });
  container.sort(function (a, b) {
    return a.toLowerCase().localeCompare(b.toLowerCase());
  });
  return container;
}

function setFirstMD(exam) {
  var firstMD = "";
  if (exam.exAttdMD !== undefined && exam.exAttdMD.length !== 0) {
    exam.exAttdMD.forEach(value => {
      value = getPhysicianName(value);
      if (firstMD == "")
        firstMD = value;
      else if (firstMD > value)
        firstMD = value;
    });
  }
  else if (exam.exRefMD !== undefined && exam.exRefMD.length !== 0) {
    exam.exRefMD.forEach(value => {
      value = getPhysicianName(value);
      if (firstMD == "")
        firstMD = value;
      else if (firstMD > value)
        firstMD = value;
    });
  }
  return firstMD;
}

export const processExams = ({ commit, getters }, response) => {
  if (!response.success) {
    commit("displayStatus", { message: "Something went wrong while loading exams", level: "error" });
    return;
  }
  let beforeLoad = Date.now();
  let exams = response.data;
  let ssoDevice = (getters.ssoExType || "").toLowerCase();
  if (ssoDevice)
    exams = exams.filter(exam => exam.exDevice.toLowerCase().includes(ssoDevice));

  let ssoDate = getters.ssoExDate ? formatDateISO(getters.ssoExDate) : "";
  if (ssoDate)
    exams = exams.filter(exam => formatDateISO(exam.exDate) === ssoDate);
  let ptSrNo = exams && exams.length && exams[0] && exams[0].ptSrNo;
  logDetailed("getPatientExamActions", "processExams", "execution-time", ptSrNo, "beforeLoad");

  let devices = getters.devices;
  commit("clearImageOrder");
  exams.forEach(exam => {
    exam.moment = moment(exam.exDate);
    exam.dateDisplay = formatDateDisplay(exam.exDate);
    exam.dateIso = formatDateISO(exam.exDate);
    exam.listText = `${exam.dateDisplay} : ${exam.exDevice} [${exam.exSrNo}]`;
    exam.odGroups = [];
    exam.osGroups = [];
    exam.browserType = "";
    exam.octDateButtonText = "";
    exam.selectAllImages = false;
    exam.loadedImageMetadata = false;
    exam.devSrNo = exam.exDevType;

    let device = devices.find(d => d.devSrNo == exam.devSrNo);
    if (device) {
      exam.devSrNo = device.devSrNo;
      exam.manufacturer = device.manufacturer;
      exam.isOptos = device.isOptos;
      exam.isOCT = device.isOCT;
      exam.isScans = device.isScans;
      exam.orderByLaterality = device.orderByLaterality;
    }
    exam.exAttdMDHtml = generatePhysicianNameArray(exam.exAttdMD);
    exam.exRefMDHtml = generatePhysicianNameArray(exam.exRefMD);
    exam.firstMD = setFirstMD(exam);
  });
  commit("setExams", exams);
  let elapsed = Date.now() - beforeLoad;
  logDetailed("getPatientExamActions", "processExams", "execution-time", ptSrNo, "whole procedure", elapsed);
};

export const getOCTExamInfo = ({ commit, getters }) =>
  axios
    .post("/api/octexaminfo/", getters.exams.map(e => e.exSrNo))
    .then(data => commit("updateOCTExamInfo", data.data));

export const loadExamImageMetadata = ({ commit, dispatch, getters }, { exSrNos, forceLoad }) => {
  logDetailed("getPatientExamActions", "loadExamImageMetadata", "execution-time", "beforeLoad");
  let beforeLoad = Date.now();
  if (!exSrNos) {
    let exams = getters.selectedExams;
    if (!exams || !exams.length) exams = getters.exams;

    exSrNos = exams
      .filter(e => forceLoad || e.loadedImageMetadata == false)
      .map(e => e.exSrNo);
  } else {
    exSrNos = exSrNos.filter(exSrNo => forceLoad || !getters.examBySrNo(exSrNo).loadedImageMetadata);
  }

  if (!exSrNos || !exSrNos.length) 
    return;
  
  let promise = axios.post("api/exam/images", exSrNos)
    .then(response => {
      let elapsed = Date.now() - beforeLoad;
      logDetailed("getPatientExamActions", "loadExamImageMetadata", "execution-time", "api call", elapsed);
      // build exImgDataRes groupings
      let items = [];
      for (let i = 0; i < response.data.length; ++i) {
        let result = response.data[i];
        var match = items.filter(e => e.exSrNo == result.exSrNo);
        if (match && match[0]) {
          match[0].imgData.push(result);
        } else {
          items.push({ exSrNo: result.exSrNo, imgData: [result] });
        }
      }

      for (let item of items)
        dispatch("processExamImages", item);
    }).catch(error => Promise.reject(error.response));

  promise.finally(() => {
    let elapsed = Date.now() - beforeLoad;
    if (iPadBrowser)
      commit("finishedLoadingAppPrintImages");
    logDetailed("getPatientExamActions", "loadExamImageMetadata", "execution-time", "whole procedure", elapsed);
  })

  return promise;
}

export const processExamImages = ({ commit, getters }, { exSrNo, exam, imgData }) => {
  if (!exam)
    exam = getters.examBySrNo(exSrNo);
  if (!exSrNo)
    exSrNo = exam.exSrNo;

  logDetailed("getPatientExamActions", "processExamImages", "execution-time", exam.ptSrNo, exam.exSrNo, "beforeLoad");
  let beforeLoad = Date.now();

  if(exam.orderByLaterality){
    imgData.sort((a, b) => a.imgEye.localeCompare(b.imgEye));
  }
  
  let data = {
    exImgDataRes: {
      exSrNo: exSrNo,
      exImgData: imgData
    },
    exDevice: exam.exDevice,
    exDate: exam.exDate,
    dateDisplay: exam.dateDisplay,
    moment: exam.moment
  };
  commit("storeImages", data);
  commit("setExamImageMetadataLoaded", exSrNo);
  let elapsed = Date.now() - beforeLoad;
  logDetailed("getPatientExamActions", "processExamImages", "execution-time", exam.ptSrNo, exam.exSrNo, "whole procedure", elapsed);
}

export const loadImageMetadataForAllExams = ({ commit, dispatch }, forceLoad) => {
  commit("setLoadingMessage", "Loading exam image records");
  logDetailed("getPatientExamActions", "loadImageMetadataForAllExams", "execution-time", "beforeLoad");
  let beforeLoad = Date.now();
  return dispatch("loadExamImageMetadata", {forceLoad})
    .finally(() => {
      let elapsed = Date.now() - beforeLoad;
      logDetailed("getPatientExamActions", "loadImageMetadataForAllExams", "execution-time", "whole procedure", elapsed);
      commit("setLoadingMessage", null)
    });
}

export const getEMRStat = (context, payload) => {
  logDetailed("actions", "getEMRStat", payload);
  if(!payload.emrpro) return Promise.reject();
  let promise = axios.post("/api/exam/getemrstat", payload)
  .then(response =>{
    if (!response.data) return Promise.reject();
    return Promise.resolve(response);
  });
  return promise;
}